import { isDigitalWellBeingUrl } from './productHelpers'

export function getQueryParams(url) {
  const query = url.split('?')[1]

  if (!query) {
    return {}
  }

  const queryString = query.split('&')
  const queries = queryString.reduce((accum, bondedQuery) => {
    const pairs = bondedQuery.split('=')
    const key = pairs[0]
    const value = pairs[1]

    return { ...accum, [key]: value }
  }, {})

  return queries
}

export const stripLastRouteSlug = path => {
  let modifiedPath = path
  const lastCharacter = path.slice(-1)

  if (lastCharacter === '/') {
    modifiedPath = modifiedPath.slice(0, -1)
  }

  const pathSlugs = modifiedPath.split('/')
  pathSlugs.pop()

  const joinedPath = pathSlugs.join('/')

  return joinedPath
}

export function getSlug(title) {
  return title
    .toLowerCase()
    .split(' ')
    .join('-')
}

/**
 * Checks if the current URL contains the word "preview" (case-insensitive)
 * Used for tier-2 preview mode detection
 * @returns {boolean} True if the URL contains "preview", false otherwise
 */
export const isTier2PreviewMode = () => {
  return (
    (isTier2Url() &&
      window.location?.href?.toLowerCase().includes('preview')) ||
    false
  )
}

/**
 * Checks if the current URL contains tier-2 in the pathname
 * @returns {boolean} True if the URL contains "/tier-2", false otherwise
 */
export const isTier2Url = () => {
  return window.location?.pathname?.includes('/tier-2') || false
}

/**
 * Returns true when the current route is any preview route — i.e. the :verb
 * URL segment is "preview" — regardless of the surrounding product path.
 *
 * Covers both native DWB paths:
 *   /product/digital-well-being/lessons/preview/:grade/:lesson
 * and program-scoped paths:
 *   /product/:program/lessons/digital-well-being/preview/:grade/:lesson
 * as well as tier-2 preview mode (which uses a query param rather than a path
 * segment).
 *
 * Use this instead of isDigitalWellBeingPreviewMode() in contexts that should
 * skip LMS calls unconditionally on any preview route.
 *
 * @returns {boolean}
 */
export const isPreviewRoute = () => {
  const pathname = window.location?.pathname || ''
  return pathname.includes('/preview/') || isTier2PreviewMode()
}

/**
 * Checks if the current pathname is on a digital well-being preview route.
 *
 * Combines {@link isPreviewRoute} with a DWB path check so callers can detect
 * DWB-specific preview context without matching other programs' preview routes.
 *
 * Covers native DWB paths:
 *   /product/digital-well-being/lessons/preview/:grade/:lesson
 * and program-scoped paths:
 *   /product/:program/lessons/digital-well-being/preview/:grade/:lesson
 *
 * @returns {boolean}
 */
export const isDigitalWellBeingPreviewMode = () =>
  isDigitalWellBeingUrl() && isPreviewRoute()

export const DWB_PROGRAM_SUPPORTS_BASE_PATH =
  '/product/digital-well-being/program-supports'

export const DWB_PROGRAM_SUPPORTS_ARTICLE = `${DWB_PROGRAM_SUPPORTS_BASE_PATH}/:articleSlug`

export function resolveDwbProgramSupportsArticleSlug(routeValue = '') {
  const normalized = String(routeValue || '').trim()
  if (!normalized) return ''

  if (normalized.startsWith(`${DWB_PROGRAM_SUPPORTS_BASE_PATH}/`)) {
    return normalized.slice(DWB_PROGRAM_SUPPORTS_BASE_PATH.length + 1)
  }

  if (normalized.startsWith('/program-supports/')) {
    return normalized.replace(/^\/program-supports\//, '')
  }

  if (normalized.startsWith('/digital-well-being/program-supports/')) {
    return normalized.replace(/^\/digital-well-being\/program-supports\//, '')
  }

  return normalized.replace(/^\//, '')
}

export function buildDwbProgramSupportsBrowserPath(articleSlug = '') {
  const slug = resolveDwbProgramSupportsArticleSlug(articleSlug)
  return slug ? `${DWB_PROGRAM_SUPPORTS_BASE_PATH}/${slug}` : ''
}

export function buildDwbProgramSupportsCurriculumRoutePath(articleSlug = '') {
  const slug = resolveDwbProgramSupportsArticleSlug(articleSlug)
  return slug ? `/program-supports/${slug}` : '/program-supports'
}
